import type { EarlierConversation } from "../../../skills/leadcue-reply/scripts/lib/earlier-conversation.ts";
import { appCount, appDate } from "./app-dates.ts";

/*
 * What the thread says about the lead's earlier conversation, for a reply to a follow-up campaign
 * (`earlier-conversation.ts`, `web/earlier-conversation.tsx`). Pure, so the words are tested apart
 * from the page.
 */

/** Which earlier campaign, by Bison's name where it was read. */
export function earlierCampaignName(conversation: EarlierConversation): string {
  const names = conversation.campaigns.flatMap((campaign) =>
    campaign.name?.trim() ? [campaign.name.trim()] : [],
  );
  if (names.length === conversation.campaigns.length && names.length > 0) return names.join(", ");
  return conversation.campaigns.length > 1 ? "Earlier campaigns" : "An earlier campaign";
}

/** "3 messages, 12 Jun 2026 to 3 Jul 2026", or one date when they share a day. */
export function earlierSpan(conversation: EarlierConversation): string {
  const count = conversation.messages.length;
  const first = conversation.messages[0]?.date_received;
  const last = conversation.messages.at(-1)?.date_received;
  const messages = `${appCount(count)} ${count === 1 ? "message" : "messages"}`;
  if (!first || !last) return messages;
  const from = appDate(first);
  const to = appDate(last);
  return from === to ? `${messages}, ${from}` : `${messages}, ${from} to ${to}`;
}

/** Who wrote one earlier message, in the words of the thread. */
export function earlierAuthor(from: EarlierConversation["messages"][number]["from"]): string {
  return from === "seller" ? "Sent to lead" : "Lead wrote";
}

/** The line under the open fold: what this is for, and what it is not. */
export const EARLIER_EXPLANATION =
  "The lead's conversation from before this follow-up, as the draft could read it. It is another campaign's: its offer, prices and links are not this campaign's, and the draft may not repeat them.";

/** Older messages the draft did not read, when the bounds left some out. */
export function earlierOmitted(conversation: EarlierConversation): string | null {
  const count = conversation.omitted_messages;
  if (!count) return null;
  return `${appCount(count)} older ${count === 1 ? "message was" : "messages were"} not read.`;
}
